import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  EmailAddressesClient,
  EMAIL_PAGE_SIZE,
  usedSinceFor,
  type EmailAddressesSearch,
} from "@/features/parties/EmailAddressesClient";
import { listEmailAddresses } from "@/features/parties/emailAddresses.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { fetchSession, sessionQueryKey, hasAnyRole } from "@/lib/auth";

// Admin-only, like /parties: a UX gate; the API enforces requireRole("admin").
export const Route = createFileRoute("/_shell/email-addresses")({
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!hasAnyRole(session, ["admin"])) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search: Record<string, unknown>): EmailAddressesSearch => ({
    q: typeof search.q === "string" && search.q ? search.q : undefined,
    status: search.status === "active" || search.status === "inactive" ? search.status : undefined,
    since:
      search.since === "month" || search.since === "prevMonth" || search.since === "90d" ? search.since : undefined,
    page: typeof search.page === "string" ? Number(search.page) || undefined : undefined,
  }),
  component: EmailAddressesPage,
});

function EmailAddressesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["email-addresses", search],
    queryFn: () =>
      listEmailAddresses({
        q: search.q,
        isActive: search.status === "active" ? "true" : search.status === "inactive" ? "false" : undefined,
        usedSince: usedSinceFor(search.since),
        page: search.page,
        pageSize: EMAIL_PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  function updateParams(updates: Record<string, string>) {
    void navigate({
      to: "/email-addresses",
      search: (prev) => applyParamUpdates(prev as Record<string, string | undefined>, updates),
    });
  }

  return (
    <EmailAddressesClient
      result={query.data}
      isLoading={query.isLoading}
      search={search}
      updateParams={updateParams}
    />
  );
}
